import { BadgeVariant } from "shared/components";

export enum FraudRiskLevel {
  Low = "LOW",
  Medium = "MEDIUM",
  High = "HIGH"
}

type FraudRiskBadge = {
  variant: BadgeVariant;
  title: string;
};

export const FRAUD_RISK_BADGE_MAP: Record<FraudRiskLevel, FraudRiskBadge> = {
  [FraudRiskLevel.Low]: {
    variant: BadgeVariant.SUCCESS,
    title: "Low"
  },
  [FraudRiskLevel.Medium]: {
    variant: BadgeVariant.WARNING,
    title: "Medium"
  },
  [FraudRiskLevel.High]: {
    variant: BadgeVariant.ERROR,
    title: "High"
  }
};

type RiskAssessment = {
  risk_level?: string | null;
  summary?: string | null;
};

type InscribePayload = {
  risk_assessment?: RiskAssessment | null;
  risk_assessment_summary?: string | null;
  risk_level?: string | null;
  trust_score?: number | null;
  is_fraudulent?: boolean | null;
  is_amount_verified?: boolean | null;
  high_risk_level?: boolean | null;
  low_trust_score?: boolean | null;
  low_quality_score?: boolean | null;
  type_mismatch?: boolean | null;
  missing_period?: boolean | null;
  no_amounts_extracted?: boolean | null;
  not_w2?: boolean | null;
  wrong_year?: boolean | null;
  is_name_present?: boolean | null;
  is_employer_name_present?: boolean | null;
};

type FormAuthenticity = {
  score?: number | null;
};

type FormAnalysis = {
  form_authenticity?: FormAuthenticity | null;
};

type FormAnalysisContainer = {
  form_analysis?: (FormAnalysis | null)[] | null;
};

type FraudRiskSource = {
  raw_data?: InscribePayload | null;
  additional_data?: InscribePayload | null;
  sources?: ({ trust_score?: number | null } | null)[] | null;
  trust_score?: number | null;
  rejection_reason?: string[] | string | null;
  doc_analysis?: FormAnalysisContainer | null;
  form_analysis?: (FormAnalysis | null)[] | null;
};

// Ocrolus doesn't return a categorical risk level like Inscribe does, only a
// numeric authenticity score. Thresholds per product: >=80 Low, <=60 High
// (aligned with the "potential fraud" cutoff), otherwise Medium.
const OCROLUS_LOW_RISK_MIN_SCORE = 80;
const OCROLUS_HIGH_RISK_MAX_SCORE = 60;

const toOcrolusRiskLevel = (score?: number | null): FraudRiskLevel | null => {
  if (typeof score !== "number") return null;

  if (score >= OCROLUS_LOW_RISK_MIN_SCORE) return FraudRiskLevel.Low;
  if (score <= OCROLUS_HIGH_RISK_MAX_SCORE) return FraudRiskLevel.High;

  return FraudRiskLevel.Medium;
};

const resolveOcrolusDocScore = (
  row?: FraudRiskSource | null
): number | null => {
  const score =
    row?.doc_analysis?.form_analysis?.[0]?.form_authenticity?.score ??
    row?.form_analysis?.[0]?.form_authenticity?.score;

  return typeof score === "number" ? score : null;
};

const hasInscribeRejectionSignal = (
  source?: InscribePayload | null
): boolean => {
  if (!source) return false;
  return Boolean(
    source.is_fraudulent ||
    source.high_risk_level ||
    source.low_trust_score ||
    source.low_quality_score ||
    source.type_mismatch ||
    source.missing_period ||
    source.no_amounts_extracted ||
    source.not_w2 ||
    source.wrong_year ||
    source.is_name_present === false ||
    source.is_employer_name_present === false
  );
};

const toRiskLevel = (value?: string | null): FraudRiskLevel | null => {
  if (!value) return null;

  const normalized = value.toUpperCase();

  if (normalized === FraudRiskLevel.Low) return FraudRiskLevel.Low;
  if (normalized === FraudRiskLevel.Medium) return FraudRiskLevel.Medium;
  if (normalized === FraudRiskLevel.High) return FraudRiskLevel.High;

  return null;
};

const toBooleanRiskLevel = (value?: boolean | null): FraudRiskLevel | null => {
  if (typeof value !== "boolean") return null;

  return value ? FraudRiskLevel.High : FraudRiskLevel.Low;
};

const firstOf = <T>(
  sources: (InscribePayload | null | undefined)[],
  pick: (source: InscribePayload) => T | null
): T | null => {
  for (const source of sources) {
    if (!source) continue;

    const value = pick(source);
    if (value !== null) return value;
  }

  return null;
};

const hasRejectionReason = (row: FraudRiskSource): boolean => {
  const { rejection_reason } = row;
  if (!rejection_reason) return false;
  if (typeof rejection_reason === "string") return rejection_reason.length > 0;
  return rejection_reason.length > 0;
};

export const resolveFraudRisk = (row: unknown): FraudRiskLevel | null => {
  const source = (row ?? {}) as FraudRiskSource;
  const { raw_data, additional_data } = source;
  const inscribeSources = [raw_data, additional_data];

  return (
    firstOf(
      inscribeSources,
      (payload) =>
        toRiskLevel(payload.risk_assessment?.risk_level) ??
        toRiskLevel(payload.risk_level)
    ) ??
    firstOf(inscribeSources, (payload) =>
      toBooleanRiskLevel(payload.is_fraudulent)
    ) ??
    (inscribeSources.some(hasInscribeRejectionSignal) ||
    hasRejectionReason(source)
      ? FraudRiskLevel.High
      : null) ??
    toOcrolusRiskLevel(resolveAuthenticityScore(source))
  );
};

export const resolveAuthenticityScore = (row: unknown): number | null => {
  const source = (row ?? {}) as FraudRiskSource;
  const { raw_data, additional_data, sources, trust_score } = source;

  for (const payload of [raw_data, additional_data])
    if (typeof payload?.trust_score === "number") return payload.trust_score;

  if (typeof trust_score === "number") return trust_score;

  for (const inscribeSource of sources ?? [])
    if (typeof inscribeSource?.trust_score === "number")
      return inscribeSource.trust_score;

  return resolveOcrolusDocScore(source);
};

type AssetSource = {
  asset?: { asset?: { url?: string | null } | null } | null;
};

export const resolveDocumentUrl = (row: unknown): string | undefined =>
  (row as AssetSource)?.asset?.asset?.url ?? undefined;

export const resolveRiskSummary = (row: unknown): string | null => {
  const { raw_data, additional_data } = (row ?? {}) as FraudRiskSource;

  for (const source of [additional_data, raw_data]) {
    const summary =
      source?.risk_assessment_summary ?? source?.risk_assessment?.summary;
    if (summary) return summary;
  }

  return null;
};

const AMOUNT_NOT_VERIFIED = "amount_not_verified";

export const isAmountNotVerified = (row: unknown): boolean => {
  const { additional_data, raw_data, rejection_reason } = (row ??
    {}) as FraudRiskSource;

  const reasons =
    typeof rejection_reason === "string"
      ? [rejection_reason]
      : (rejection_reason ?? []);

  if (reasons.includes(AMOUNT_NOT_VERIFIED)) return true;

  for (const source of [additional_data, raw_data])
    if (source?.is_amount_verified === false) return true;

  return false;
};
